// The conversions behind the five Insights sections.
//
// Small file, three traps:
//
//   CLICK RATE arrives as `clickRate` on a 0–1 scale, with an optional
//   `clickThroughRate` kept for back-compat that is on 0–100. Reading the
//   wrong one renders 0.25 as "0%" or 25 as "2500%". Both are plausible on
//   sight, which is exactly why this is a function with a test rather than an
//   inline `Math.round(x * 100)` at four call sites.
//
//   HELPFULNESS finally has a denominator here. `ArticleEffectiveness` carries
//   `helpfulCount` and `notHelpfulCount`, so this is the one screen in the
//   knowledge base that can honour the rule the register could not: a share
//   and the base it was taken over travel together, or neither is shown.
//
//   AN UNRATED ARTICLE is not a 0% article. `helpfulnessScore` is 0 both when
//   two people voted no and when nobody voted at all, and those are opposite
//   facts about a piece of writing.

import type {
  ArticleEffectiveness,
  EmbeddingStatus,
  PopularSearch,
} from '@services/knowledge-analytics.service';

/**
 * A search's click-through, as a percentage, from whichever field is present.
 *
 * `clickRate` (0–1) is preferred because it is what the API documents today;
 * `clickThroughRate` (0–100) is only consulted when the modern field is
 * absent, never when it is present-and-zero.
 */
export function clickRatePct(search: Pick<PopularSearch, 'clickRate' | 'clickThroughRate'>): number | null {
  const { clickRate, clickThroughRate } = search;
  if (typeof clickRate === 'number' && Number.isFinite(clickRate)) {
    return Math.round(clickRate * 100);
  }
  if (typeof clickThroughRate === 'number' && Number.isFinite(clickThroughRate)) {
    return Math.round(clickThroughRate);
  }
  return null;
}

export interface Helpfulness {
  /** Whole percent. */
  pct: number;
  helpful: number;
  rated: number;
}

/**
 * An article's helpfulness, or null when nobody has rated it.
 *
 * Derived from the counts rather than trusting `helpfulnessScore`, so the
 * percentage and the denominator can never disagree on screen.
 */
export function helpfulnessOf(
  article: Pick<ArticleEffectiveness, 'helpfulCount' | 'notHelpfulCount'>,
): Helpfulness | null {
  const helpful = Math.max(0, article.helpfulCount ?? 0);
  const rated = helpful + Math.max(0, article.notHelpfulCount ?? 0);
  if (rated === 0) return null;
  return { pct: Math.round((helpful / rated) * 100), helpful, rated };
}

/** '62% · 8 of 13', or the absence stated plainly. */
export function helpfulnessText(article: Pick<ArticleEffectiveness, 'helpfulCount' | 'notHelpfulCount'>): string {
  const score = helpfulnessOf(article);
  return score === null ? 'No ratings' : `${score.pct}% · ${score.helpful} of ${score.rated}`;
}

/**
 * What a source is doing, as one word.
 *
 * `stale` is NOT one of the queue's statuses — the payload has no such value.
 * It is `needsRefresh` on a row that has otherwise finished, and it is derived
 * here so the four buckets the screen shows are the four a reader can act on.
 */
export type EmbedState = 'embedded' | 'pending' | 'stale' | 'failed';

export function embedState(row: EmbeddingStatus): EmbedState {
  switch (row.embeddingStatus) {
    case 'completed':
      // Finished, but the text moved underneath it. The assistant still
      // answers from this source — just from the version it embedded.
      return row.needsRefresh ? 'stale' : 'embedded';
    case 'failed':
      return 'failed';
    case 'pending':
    case 'processing':
      return 'pending';
    default:
      // An unrecognised status is still working, never ready. Being wrong
      // towards ready costs a search that silently returns nothing.
      return 'pending';
  }
}

export interface EmbedTally {
  /** Finished and current. */
  embedded: number;
  pending: number;
  /** Finished but drifted — a SUBSET of what the API calls embedded. */
  stale: number;
  failed: number;
  total: number;
}

/**
 * What the per-source report adds up to.
 *
 * Counted from the rows rather than read off the dashboard summary because the
 * summary cannot name which source is which — not because the two disagree.
 * They do not: the summary's `embeddedCount` counts everything completed and
 * its `staleCount` counts everything needing a refresh, and a stale source is
 * completed, so the two overlap by design (knowledge-service #9103).
 */
export function embedTally(rows: EmbeddingStatus[]): EmbedTally {
  const tally: EmbedTally = { embedded: 0, pending: 0, stale: 0, failed: 0, total: rows.length };
  for (const row of rows) tally[embedState(row)] += 1;
  return tally;
}

/**
 * The same rows, read in the HEALTH ENDPOINT'S vocabulary.
 *
 * `embedTally` above partitions the sources into four buckets that do not
 * overlap, which is the right shape for "what do I fix". This is the other
 * shape — the one `/embeddings/health` returns, and the one the original
 * Embedding Health Status panel showed:
 *
 *   embeddedCount = everything that FINISHED, stale ones included
 *   staleCount    = everything flagged needsRefresh — a SUBSET of the above
 *
 * So `embeddedCount + staleCount` deliberately exceeds `totalSources`, and a
 * screen showing both has to say so or the reader will try to add them up. On
 * nine sources with five drifted, this reports 9 embedded and 5 stale where
 * `embedTally` reports 4 embedded and 5 stale. Neither is wrong; they answer
 * different questions.
 *
 * Derived here from the per-source rows rather than fetched, because
 * `getEmbeddingHealth` reduces that very same unfiltered list server-side
 * (`getEmbeddingStatus`, no limit) — one source of truth, and no second
 * endpoint that can disagree with the table under it.
 */
export interface EmbedHealth {
  totalSources: number;
  /** Finished, INCLUDING the ones that have since drifted. */
  embeddedCount: number;
  pendingCount: number;
  failedCount: number;
  /** `needsRefresh`. A subset of `embeddedCount`, not a sibling of it. */
  staleCount: number;
  /** 0-100, on the service's own weighting: 70 embedded, 20 clean, 10 fresh. */
  healthScore: number;
}

export function embedHealth(rows: EmbeddingStatus[]): EmbedHealth {
  const totalSources = rows.length;
  const embeddedCount = rows.filter((r) => r.embeddingStatus === 'completed').length;
  const pendingCount = rows.filter(
    (r) => r.embeddingStatus === 'pending' || r.embeddingStatus === 'processing',
  ).length;
  const failedCount = rows.filter((r) => r.embeddingStatus === 'failed').length;
  const staleCount = rows.filter((r) => r.needsRefresh).length;

  // Nothing to embed is not a failure. The service returns 100 for an empty
  // tenant and this has to agree with it, or the figure moves when a source is
  // added rather than when anything actually changes.
  const healthScore =
    totalSources === 0
      ? 100
      : (embeddedCount / totalSources) * 70 +
        ((totalSources - failedCount) / totalSources) * 20 +
        ((totalSources - staleCount) / totalSources) * 10;

  return { totalSources, embeddedCount, pendingCount, failedCount, staleCount, healthScore };
}

/**
 * Sources that are not serving current text, worst first.
 *
 * `failed` outranks `pending`: a failed embedding will not fix itself, while a
 * pending one is a queue that is still moving. `stale` is last — the old text
 * is still being served, which is wrong but not silent, and it is the only one
 * of the three the assistant can still answer from at all.
 */
export function embedPriority(rows: EmbeddingStatus[]): EmbeddingStatus[] {
  const rank: Record<EmbedState, number> = { failed: 0, pending: 1, stale: 2, embedded: 3 };
  return rows
    .filter((r) => embedState(r) !== 'embedded')
    .slice()
    .sort(
      (a, b) =>
        rank[embedState(a)] - rank[embedState(b)] ||
        (a.title ?? '').localeCompare(b.title ?? ''),
    );
}
